import { useSnackbar } from 'notistack';
import { BulkActivationResultItemDTO, BulkActivationResultItemDTOWarningEnum } from 'probonio-shared-ui/api';

type EnqueueSnackbar = ReturnType<typeof useSnackbar>['enqueueSnackbar'];
type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export interface BulkResultMessageKeys {
  empty: string;
  successFragment: string;
}

/**
 * Shows a single combined snackbar for a bulk activation result
 */
export function enqueueBulkResultSnackbar(
  results: BulkActivationResultItemDTO[],
  t: TranslateFn,
  enqueueSnackbar: EnqueueSnackbar,
  keys: BulkResultMessageKeys,
): number {
  const successResults = results.filter(result => result.status === 'SUCCESS');
  const adjustedResults = results.filter(result => result.status === 'ADJUSTMENT');
  const blockedResults = results.filter(result => result.status === 'BLOCKED');
  const errorResults = results.filter(result => result.status === 'ERROR');
  const successCount = successResults.length;
  // ADJUSTMENT = applied but with a modified end date; BLOCKED = not applied at all
  const adjustedCount = adjustedResults.length;
  const blockedCount = blockedResults.length;
  const errorCount = errorResults.length;
  // a capped result is a SUCCESS with an adjusted endDate (status 'SUCCESS' + warning), so it is a subset of successes
  const cappedCount = successResults.filter(
    result => result.warning === BulkActivationResultItemDTOWarningEnum.TerminationDateCapped,
  ).length;

  if (results.length === 0) {
    enqueueSnackbar(t(keys.empty), { variant: 'warning' });
    return 0;
  }

  // total header + one bullet line per outcome. errors are only counted ("skipped") since the reasons vary;
  // the only extra detail is the endDate capping on the success line
  const lines: string[] = [t('bulkResult.processed', { count: results.length })];
  if (successCount) {
    const capped = cappedCount ? ` (${t('bulkResult.cappedSuffix', { count: cappedCount })})` : '';
    lines.push(`• ${t(keys.successFragment, { count: successCount })}${capped}`);
  }
  if (adjustedCount) {
    lines.push(`• ${t('bulkResult.adjusted', { count: adjustedCount })}`);
  }
  if (blockedCount) {
    lines.push(`• ${t('bulkResult.blocked', { count: blockedCount })}`);
  }
  if (errorCount) {
    lines.push(`• ${t('bulkResult.skipped', { count: errorCount })}`);
  }

  // a change was applied (fully or adjusted); ERROR/BLOCKED mean nothing was applied for that employee
  const appliedCount = successCount + adjustedCount;
  const notAppliedCount = errorCount + blockedCount;
  // green only when everything applied cleanly; warning when something was applied but adjusted/blocked/failed; error when nothing applied
  const variant = notAppliedCount ? (appliedCount ? 'warning' : 'error') : cappedCount || adjustedCount ? 'warning' : 'success';
  enqueueSnackbar(lines.join('\n'), { variant, style: { whiteSpace: 'pre-line' }, autoHideDuration: 10000 });

  return appliedCount;
}
